import React from 'react';
import { Box, Typography, List, ListItem, ListItemIcon, IconButton, ListItemText, Menu, MenuItem, Divider, useTheme } from '@mui/material';
import { Layers, Visibility, VisibilityOff, Lock, LockOpen, Delete, Add } from '@mui/icons-material';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const SortableRow = ({ layer, selected, onSelect, onToggleVisible, onToggleLock, onContextMenu }) => {
  const theme = useTheme();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: layer.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
    backgroundColor: selected ? theme.palette.action.selected : 'transparent',
    cursor: 'grab'
  };
  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onContextMenu={(e) => onContextMenu(e, layer.id)}
      sx={{ '&:hover': { bgcolor: theme.palette.action.hover } }}
      onClick={() => onSelect?.(layer)}
    >
      <ListItemIcon sx={{ minWidth: 30 }}>
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onToggleVisible?.(layer); }} sx={{ color: theme.palette.text.primary, p: 0.5 }}>
          {layer.visible ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
        </IconButton>
      </ListItemIcon>
      <ListItemText primary={layer.name} secondary={layer.type} sx={{ '& .MuiListItemText-primary': { color: theme.palette.text.primary }, '& .MuiListItemText-secondary': { color: theme.palette.text.secondary } }} />
      <IconButton size="small" onClick={(e) => { e.stopPropagation(); onToggleLock?.(layer); }} sx={{ color: theme.palette.text.primary }}>
        {layer.locked ? <Lock fontSize="small" /> : <LockOpen fontSize="small" />}
      </IconButton>
    </ListItem>
  );
};

const LayersPanel = ({ layers = [], selectedLayer, onSelect, onToggleLock, onToggleVisible, onDelete, onAdd, onReorder, onContextMoveUp, onContextMoveDown, onContextRename, onDndStart, onDndEnd }) => {
  const theme = useTheme();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  // Context menu
  const [menu, setMenu] = React.useState(null); // { mouseX, mouseY, layerId } | null
  const openMenu = (e, id) => {
    e.preventDefault();
    setMenu({ mouseX: e.clientX - 2, mouseY: e.clientY - 4, layerId: id });
  };
  const closeMenu = () => setMenu(null);
  const actId = menu?.layerId;

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      onDndEnd?.();
      return;
    }
    const ids = layers.map(l => l.id);
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const newOrder = arrayMove(layers, oldIndex, newIndex);
    console.log('[LayersPanel] onDragEnd order ids:', newOrder.map(l => l.id));
    onReorder?.(newOrder);
    onDndEnd?.();
  };

  return (
    <Box sx={{ width: '100%', bgcolor: theme.palette.background.paper, display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
      <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" sx={{ color: theme.palette.text.primary, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Layers /> Layers
        </Typography>
      </Box>
      {/* Scrollable list area with fixed height inside the panel */}
      <Box sx={{ height: 280, overflowY: 'auto', overflowX: 'hidden' }}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={onDndStart} onDragEnd={onDragEnd}>
          <SortableContext items={layers.map(l => l.id)} strategy={verticalListSortingStrategy}>
            <List dense sx={{ py: 0, m: 0 }}>
              {layers.map((layer) => (
                <SortableRow
                  key={layer.id}
                  layer={layer}
                  selected={selectedLayer === layer.object}
                  onSelect={onSelect}
                  onToggleVisible={onToggleVisible}
                  onToggleLock={onToggleLock}
                  onContextMenu={openMenu}
                />
              ))}
            </List>
          </SortableContext>
        </DndContext>
        <Menu
          open={!!menu}
          onClose={closeMenu}
          anchorReference="anchorPosition"
          anchorPosition={menu ? { top: menu.mouseY, left: menu.mouseX } : undefined}
        >
          <MenuItem onClick={() => { const layer = layers.find(l => l.id === actId); if (layer) onContextRename?.(actId, layer); closeMenu(); }}>Rename…</MenuItem>
          <MenuItem onClick={() => { onContextMoveUp?.(actId); closeMenu(); }}>Move Up</MenuItem>
          <MenuItem onClick={() => { onContextMoveDown?.(actId); closeMenu(); }}>Move Down</MenuItem>
          <Divider />
          <MenuItem onClick={() => { const layer = layers.find(l => l.id === actId); if (layer) onDelete?.(layer); closeMenu(); }}>Delete</MenuItem>
        </Menu>
      </Box>
      <Box sx={{ p: 1, borderTop: `1px solid ${theme.palette.divider}`, display: 'flex', justifyContent: 'space-between' }}>
        <IconButton size="small" onClick={onDelete} sx={{ color: theme.palette.text.primary }} title="Delete selected layer">
          <Delete />
        </IconButton>
        <IconButton size="small" onClick={(e) => onAdd?.(e.currentTarget)} sx={{ color: theme.palette.text.primary }} title="Add new layer">
          <Add />
        </IconButton>
      </Box>
    </Box>
  );
};

export default LayersPanel;
